import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function ServiceCard({
    index,
    title,
    desc,
    tag,
    href,
    base,
    action,
    testid,
}) {
    const isTouch = useState(
        () =>
            typeof window !== "undefined" &&
            window.matchMedia("(hover: none), (pointer: coarse)").matches,
    )[0];
    const [active, setActive] = useState(false);
    const ref = useRef(null);
    const inView = useInView(ref, { amount: 0.35 });
    const state = active ? "on" : "off";

    useEffect(() => {
        if (!isTouch || !inView) return undefined;
        const id = setInterval(() => setActive((a) => !a), 2800);
        return () => clearInterval(id);
    }, [isTouch, inView]);

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.85, ease: EASE, delay: (index % 2) * 0.1 }}
            className="h-full"
        >
            <Link
                to={href}
                data-testid={testid}
                onMouseEnter={() => !isTouch && setActive(true)}
                onMouseLeave={() => !isTouch && setActive(false)}
                className={`group relative block aspect-[4/3] overflow-hidden rounded-xl border bg-carbon transition-colors duration-500 lg:aspect-[16/10] ${
                    active ? "border-flame/60" : "border-line"
                }`}
                style={{
                    boxShadow: active
                        ? "0 0 40px rgba(255, 85, 0, 0.18)"
                        : "none",
                }}
            >
                <motion.img
                    src={base}
                    alt={title}
                    initial={false}
                    animate={{ scale: active ? 1.07 : 1 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover"
                />

                <motion.div
                    className="absolute inset-0"
                    initial={false}
                    animate={state}
                    variants={{
                        off: { clipPath: "inset(0% 0% 0% 100%)" },
                        on: { clipPath: "inset(0% 0% 0% 0%)" },
                    }}
                    transition={{ duration: 0.55, ease: [0.83, 0, 0.17, 1] }}
                >
                    <motion.img
                        src={action}
                        alt=""
                        initial={false}
                        animate={{ scale: active ? 1 : 1.14 }}
                        transition={{ duration: 1.2, ease: EASE }}
                        className="h-full w-full object-cover"
                    />
                </motion.div>

                <motion.div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-flame mix-blend-overlay"
                    initial={false}
                    animate={state}
                    variants={{ off: { opacity: 0 }, on: { opacity: [0, 0.3, 0] } }}
                    transition={{ duration: 0.5, times: [0, 0.25, 1] }}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/35 to-ink/25" />

                <div className="absolute inset-x-0 top-0 flex items-start justify-between p-6 lg:p-8">
                    <span className="font-mono-tech text-sm font-bold tracking-[0.25em] text-flame">
                        {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="hidden font-mono-tech text-[10px] tracking-[0.3em] text-mist sm:block">
                        {tag}
                    </span>
                </div>

                <div className="absolute right-5 top-14 flex h-11 w-11 items-center justify-center rounded-full border border-paper/25 bg-ink/40 backdrop-blur-sm transition-all duration-300 group-hover:border-flame group-hover:bg-flame lg:right-7">
                    <ArrowUpRight
                        className={`h-5 w-5 transition-all duration-300 ${
                            active
                                ? "translate-x-0.5 -translate-y-0.5 text-ink"
                                : "text-paper group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink"
                        }`}
                    />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                    <h3 className="font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-paper sm:text-3xl lg:text-4xl">
                        {title}
                    </h3>
                    <div
                        className={`grid transition-all duration-500 ease-out ${
                            active
                                ? "mt-3 grid-rows-[1fr] opacity-100"
                                : "mt-0 grid-rows-[0fr] opacity-0 sm:group-hover:mt-3 sm:group-hover:grid-rows-[1fr] sm:group-hover:opacity-100"
                        }`}
                    >
                        <p className="overflow-hidden text-sm leading-relaxed text-paper/75">
                            <span className="block max-w-md">{desc}</span>
                        </p>
                    </div>
                </div>

                <span
                    className={`absolute bottom-0 left-0 h-[3px] w-full origin-left bg-flame transition-transform duration-500 ${
                        active ? "scale-x-100" : "scale-x-0"
                    }`}
                />
            </Link>
        </motion.div>
    );
}